import { fontFaceSchema, type FontFaceDescriptor } from "./fonts";
import {
  parseProject,
  styleSchema,
  uid,
  type Project,
  type DesignNode,
  type Page,
  type TextStyle,
  type Typography,
  type TypographyOverrides,
} from "./schema";
export const typographyKeys = [
  "fontFamily",
  "fontFaceId",
  "fontSize",
  "fontWeight",
  "color",
  "lineHeight",
  "align",
] as const;
const own = (value: object, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key);
export function styleTypography(
  project: Project,
  style: TextStyle,
): Typography {
  const { id, name, role, inheritTypography, overrides, ...legacy } = style;
  return {
    ...project.documentSettings.typography,
    ...(inheritTypography ? {} : legacy),
    ...overrides,
  };
}
function finishFont(
  project: Project,
  properties: Typography,
  inherited: boolean,
  strict: boolean,
): Typography {
  if (!properties.fontFaceId) return { ...properties, fontFaceId: null };
  const face = project.fontManifest.find((f) => f.id === properties.fontFaceId);
  if (!face)
    throw new Error(`Missing managed font snapshot: ${properties.fontFaceId}`);
  if (inherited) {
    if (properties.fontFamily !== face.family)
      return { ...properties, fontFaceId: null };
    if (properties.fontWeight !== face.weight) {
      const variant = project.fontManifest.find(
        (f) =>
          f.family === face.family &&
          f.weight === properties.fontWeight &&
          f.style === face.style,
      );
      if (variant)
        return {
          ...properties,
          fontFaceId: variant.id,
          fontFamily: variant.family,
          fontWeight: variant.weight,
        };
      if (strict)
        throw new Error(
          `Missing managed font variant: ${face.family}, ${properties.fontWeight}, ${face.style}`,
        );
      return properties;
    }
  }
  return { ...properties, fontFamily: face.family, fontWeight: face.weight };
}
export function effectiveNode(
  project: Project,
  node: DesignNode,
  strictFonts = false,
): DesignNode {
  const style = node.styleId
    ? project.styles.find((s) => s.id === node.styleId)
    : undefined;
  if (node.styleId && !style)
    throw new Error(`Unknown text style: ${node.styleId}`);
  const base: Typography =
    node.typographyMode === "inherit"
      ? project.documentSettings.typography
      : {
          fontFamily: node.fontFamily,
          fontFaceId: node.fontFaceId ?? null,
          fontSize: node.fontSize,
          fontWeight: node.fontWeight,
          color: node.color,
          lineHeight: node.lineHeight,
          align: node.align,
        };
  const styleProps: TypographyOverrides = style
    ? style.inheritTypography
      ? style.overrides
      : Object.fromEntries(
          typographyKeys
            .filter((key) => own(style, key))
            .map((key) => [key, style[key]]),
        )
    : {};
  const properties = {
    ...base,
    ...(style?.inheritTypography ? project.documentSettings.typography : {}),
    ...styleProps,
    ...(style?.inheritTypography ? {} : style?.overrides),
    ...node.typographyOverrides,
  };
  const inheritedFace =
    !own(node.typographyOverrides, "fontFaceId") &&
    !own(styleProps, "fontFaceId") &&
    !own(style?.overrides || {}, "fontFaceId") &&
    (node.typographyMode === "inherit" || !!style?.inheritTypography);
  const resolved = finishFont(project, properties, inheritedFace, strictFonts);
  return { ...node, ...resolved, fontFaceId: resolved.fontFaceId || undefined };
}
export function patchNodeTypography(
  project: Project,
  node: DesignNode,
  patch: Partial<DesignNode>,
): DesignNode {
  const candidate = { ...node, ...patch };
  if (candidate.styleId || candidate.typographyMode === "inherit") {
    const properties: TypographyOverrides = {},
      rest = { ...patch };
    for (const key of typographyKeys)
      if (own(patch, key)) {
        Object.assign(properties, {
          [key]: patch[key] ?? (key === "fontFaceId" ? null : undefined),
        });
        delete rest[key];
      }
    return {
      ...node,
      ...rest,
      typographyOverrides: {
        ...(own(patch, "typographyOverrides")
          ? patch.typographyOverrides
          : node.typographyOverrides),
        ...properties,
      },
    };
  }
  const overrides = {
    ...(own(patch, "typographyOverrides")
      ? patch.typographyOverrides
      : node.typographyOverrides),
  };
  if (!own(patch, "typographyOverrides"))
    for (const key of typographyKeys)
      if (own(patch, key)) delete overrides[key];
  return { ...node, ...patch, typographyOverrides: overrides };
}
export function detachTextStyle(
  project: Project,
  node: DesignNode,
): DesignNode {
  return {
    ...effectiveNode(project, node),
    styleId: undefined,
    typographyMode: "direct",
    typographyOverrides: {},
  };
}
export const effectiveBackground = (project: Project, page: Page) =>
  page.backgroundMode === "inherit"
    ? project.documentSettings.background
    : page.background;

export interface TextStyleSnapshot {
  style: TextStyle;
  fontManifest: FontFaceDescriptor[];
}
export function captureTextStyle(
  project: Project,
  value: unknown,
): TextStyleSnapshot {
  const style = styleSchema.parse(
    value && typeof value === "object" && "style" in value
      ? value.style
      : value,
  );
  const refs = [style.fontFaceId, style.overrides.fontFaceId].filter(
    (id): id is string => !!id,
  );
  const families = new Set(
    refs.map((id) => {
      const face = project.fontManifest.find((f) => f.id === id);
      if (!face) throw new Error(`Missing managed font snapshot: ${id}`);
      return face.family;
    }),
  );
  return {
    style: { ...style, role: undefined },
    fontManifest: structuredClone(
      project.fontManifest.filter((f) => families.has(f.family)),
    ),
  };
}
export function applyTextStyleSnapshot(
  project: Project,
  value: unknown,
  id = uid("style"),
): Project {
  const wrapped =
    value && typeof value === "object" && "style" in value
      ? (value as TextStyleSnapshot)
      : undefined;
  const style = styleSchema.parse(wrapped ? wrapped.style : value),
    manifest = [...project.fontManifest];
  for (const candidate of wrapped?.fontManifest || []) {
    const face = fontFaceSchema.parse(candidate),
      existing = manifest.find((f) => f.id === face.id);
    if (
      existing &&
      JSON.stringify(fontFaceSchema.parse(existing)) !== JSON.stringify(face)
    )
      throw new Error("Text style font snapshot conflicts with this document");
    if (!existing) manifest.push(face);
  }
  return parseProject({
    ...project,
    fontManifest: manifest,
    styles: [...project.styles, { ...style, id, role: undefined }],
  });
}

export const calendarAlignment = (node: DesignNode) =>
  node.typographyMode === "direct" &&
  !node.styleId &&
  !Object.prototype.hasOwnProperty.call(node.typographyOverrides, "align") &&
  node.align === "left"
    ? "center"
    : node.align;
